import {
  all,
  call,
  delay,
  getContext,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { damzOnline } from '../actions/initialization';
import {
  initDrawer,
  fetchDrawerNext,
  setDrawerFilter,
  maybeClearRequestsCounter,
  clearAllDrawerStreams,
  reloadDrawerStream,
} from '../actions/drawer';
import { deleteStream, initializeIfNeeded } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { roomItemStreamGroupId } from '../../constants';
import { getDrawerStreamId, roomFetchToRoomItem } from '../helpers';
import { selectors, StreamType, withCursor } from '@wix/da-shared-react/pkg/Stream';
import {
  fetchStreamContentsSupabase,
  takeStreamFetch,
} from '@wix/da-shared-react/pkg/Stream/helpers';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { SupabaseClient } from '@supabase/supabase-js';
import { getDrawerActiveFilter } from '../selectors/drawer';
import { setRoomFlag } from '../../utils/roomFlags';
import { RoomFlagNames, RoomItem, RoomFlags } from '../../types';
import ddt from '@wix/da-ddt';
import { updateDrawerRoomLastMsg, updateDrawerRoomFlagState } from '../actions/room';
import { setGlobalCounter, refreshCounter } from '../actions/counter';
import { getCounterConversations } from '../selectors/global';
import { getActiveRoomItem } from '../selectors/room';
import { realtimeConnections } from '../RealtimeConnections';

function* handleDrawerStreamInit(action: ReturnType<typeof initDrawer>) {
  const streamId = getDrawerStreamId(action.payload.filter);
  yield put(
    initializeIfNeeded(streamId, {
      streamType: StreamType.WITH_CURSOR,
      autoFetch: true,
      streamParams: {},
      itemsPerFetch: 20,
      hasMore: true,
      fetchNextCallback: fetchDrawerNext,
      groupId: roomItemStreamGroupId,
    })
  );
}

function getFetchFunction(activeFilter: string) {
  switch (activeFilter) {
    case 'watching':
      return 'get_watching_conversations';
    case 'supporters':
      return 'get_supporter_conversations';
    default:
      return 'get_conversations';
  }
}

function buildFetchParams(activeFilter: string, cursor: string | null, limit: number) {
  const baseParams: any = {
    p_limit: limit,
  };

  if (cursor) {
    const [timestamp, room_id] = cursor.split('|');
    baseParams.p_cursor_updated_at = timestamp;
    baseParams.p_cursor_room_id = room_id;
  }

  // Only get_conversations uses p_flags parameter
  if (activeFilter !== 'watching' && activeFilter !== 'supporters') {
    baseParams.p_flags = setRoomFlag(0, activeFilter as any);
  }

  return baseParams;
}

function* handleDrawerFetchNext({ payload: { streamId } }: ReturnType<typeof fetchDrawerNext>) {
  const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');

  const { cursor, limit, activeFilter } = yield select(state => ({
    cursor: withCursor.selectors.getCursor(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
    activeFilter: getDrawerActiveFilter(state),
  }));

  const isInitialFetch = !cursor;

  const functionName = getFetchFunction(activeFilter);
  const params = buildFetchParams(activeFilter, cursor, limit);

  yield fetchStreamContentsSupabase<
    | Database['damz']['Functions']['get_conversations']['Returns']
    | Database['damz']['Functions']['get_watching_conversations']['Returns']
    | Database['damz']['Functions']['get_supporter_conversations']['Returns']
  >({
    streamId,
    fetch: async () => {
      const response = await client.rpc(functionName, params);
      return response;
    },
    getStreamData: result => {
      const hasMore = result.length >= limit + 1;
      const lastItem = result[result.length - 1];

      // we fetch one more than we need to in order to get the next hasMore
      // we need to remove that one or we'll have dupes
      if (hasMore) {
        result.pop();
      }

      // Convert the result to the format expected by roomFetchToRoomItem
      const convertedResult = result.map((row: any) => ({
        ...row,
        // Add missing fields for functions that don't return them
        multi_username: row.multi_username ?? '',
        multi_icon: row.multi_icon ?? '',
      })) as Database['damz']['Functions']['get_conversations']['Returns'];

      return {
        hasMore,
        items: roomFetchToRoomItem(convertedResult),
        cursor: `${lastItem?.updated_at}|${lastItem?.room_id}`,
      };
    },
    fakeResponse: () => generateFakeConversationsResponse(),
    onSuccess: function* () {
      // Refresh counter after initial drawer fetch to pick up new requests
      // This ensures the red dot appears when a request is created, even before a message is sent
      if (isInitialFetch) {
        yield put(refreshCounter());
      }
    },
    onFailure: function* (error) {
      ddt.alert('damz', '[damz] Failed to fetch conversation list', ['damz', 'db'], error);
      realtimeConnections.setConversationFetchFailed();
    },
  });
}

/**
 * Clears all the other drawer streams so we don't keep lots of stuff in memory
 *
 * TODO will need to see if product want this later on
 */
function* handleFilterChange(action: ReturnType<typeof setDrawerFilter>) {
  const toClear: any[] = [];

  Object.keys(RoomFlagNames).forEach(filter => {
    if (filter === action.payload.filter) {
      return;
    }
    const streamId = getDrawerStreamId(filter);
    toClear.push(put(deleteStream(streamId)));
  });

  if (action.payload.filter === 'request') {
    yield put(maybeClearRequestsCounter());
  }

  yield all(toClear);
}

/**
 * Clears all drawer streams when the DAMZ page is closed
 */
function* handleClearAllDrawerStreams() {
  const toClear: any[] = [];

  Object.keys(RoomFlagNames).forEach(filter => {
    const streamId = getDrawerStreamId(filter);
    toClear.push(put(deleteStream(streamId)));
  });

  yield all(toClear);
}

/**
 * Updates the lastMsg for a room item in the active drawer stream
 */
function* handleUpdateDrawerRoomLastMsg(action: ReturnType<typeof updateDrawerRoomLastMsg>) {
  const { room_id, lastMsg } = action.payload;

  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = getDrawerStreamId(activeFilter);

  const isInitialized = yield select(selectors.isInitialized, streamId);
  if (!isInitialized) {
    return; // stream doesn't exist
  }

  const items: RoomItem[] = yield select(selectors.getItems, streamId);
  const roomToUpdate = items.find(item => item.room.room_id === room_id);

  if (!roomToUpdate) {
    return; // room not found
  }

  const updatedRoom: RoomItem = {
    ...roomToUpdate,
    lastMsg,
    room: {
      ...roomToUpdate.room,
      updated_at: new Date().toISOString(),
    },
  };

  yield put(
    withCursor.actions.replaceItem({
      streamId,
      newItem: updatedRoom,
      predicate: (item: RoomItem) => item.room.room_id === room_id,
    })
  );
}

/**
 * Updates the flag state ON THE CLIENT UI for room items in the active drawer stream
 * No server saving here!
 */
function* handleUpdateDrawerRoomFlagState(action: ReturnType<typeof updateDrawerRoomFlagState>) {
  const { room_id, flag, value } = action.payload;
  const room_ids = Array.isArray(room_id) ? room_id : [room_id];

  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = getDrawerStreamId(activeFilter);

  const isInitialized = yield select(selectors.isInitialized, streamId);
  if (!isInitialized) {
    return; // stream doesn't exist
  }

  const items: RoomItem[] = yield select(selectors.getItems, streamId);

  // If the flag is being unset and the current filter is "all" or matches the flag being unset
  // AND the activeRoomItem is the room being affected, insert it back into the drawer
  // OR if the archived flag is being set and the active filter is "archived", add it to the archived view
  if (
    (!value && (activeFilter === 'all' || activeFilter === flag)) ||
    (value && flag === 'archived' && activeFilter === 'archived')
  ) {
    // Get the active room item to check if it's the room being affected
    const activeRoomItem = yield select(getActiveRoomItem);

    if (activeRoomItem && room_ids.includes(activeRoomItem.room.room_id)) {
      // Check if the room is not already in the current stream
      const isRoomInStream = items.some(item => item.room.room_id === activeRoomItem.room.room_id);

      if (!isRoomInStream) {
        yield put(
          withCursor.actions.insertItemsAtOffsetAndSetCursor({
            streamId,
            items: [activeRoomItem],
            offset: 0,
          })
        );
      }
    }
  }
  const roomsToUpdate = items.filter(item => room_ids.includes(item.room.room_id));

  if (roomsToUpdate.length === 0) {
    return; // no rooms found
  }

  // If the active filter matches the flag being unset, remove the item from the stream
  if (!value && activeFilter === flag) {
    // Remove all matching rooms from the stream
    const itemsToRemove = roomsToUpdate.map(room => room);
    yield put(
      withCursor.actions.deleteItems({
        streamId,
        items: itemsToRemove,
        isEqual: (a: RoomItem, b: RoomItem) => a.room.room_id === b.room.room_id,
      })
    );
    return;
  }

  // Otherwise, update the flag state as before
  const replaceActions = roomsToUpdate.map(roomToUpdate => {
    const updatedRoom: RoomItem = {
      ...roomToUpdate,
      roomMeta: {
        ...roomToUpdate.roomMeta,
        flags: value
          ? roomToUpdate.roomMeta.flags | RoomFlags[flag]
          : roomToUpdate.roomMeta.flags & ~RoomFlags[flag],
      },
    };

    return put(
      withCursor.actions.replaceItem({
        streamId,
        newItem: updatedRoom,
        predicate: (item: RoomItem) => item.room.room_id === roomToUpdate.room.room_id,
      })
    );
  });

  yield all(replaceActions);
}

function* handleMaybeClearRequestsCount() {
  yield delay(2_000);
  const filter = yield select(getDrawerActiveFilter);
  if (filter === 'request') {
    try {
      const client: SupabaseClient<Database, 'damz'> = yield getContext('supaClient');
      yield client.rpc('clear_requests_counter');
      const conversations = yield select(getCounterConversations);
      yield put(setGlobalCounter(conversations, 0));
    } catch (err) {
      ddt.error('damz', 'handleMaybeClearRequestsCount failed', err);
    }
  }
}

/**
 * Reloads the current drawer stream by deleting it and reinitializing
 */
function* handleReloadDrawerStream() {
  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = getDrawerStreamId(activeFilter);

  const isInitialized = yield select(selectors.isInitialized, streamId);
  if (!isInitialized) {
    return; // stream doesn't exist, nothing to reload
  }

  // Delete the stream to clear current data
  yield put(deleteStream(streamId));

  // Reinitialize the stream using the existing handler
  yield call(handleDrawerStreamInit, initDrawer(activeFilter));
}

/**
 * Handles drawer UI
 */
export function* drawerSaga() {
  yield take(damzOnline); // wait for online to happen
  yield all([
    takeEvery(initDrawer, handleDrawerStreamInit),
    takeStreamFetch(fetchDrawerNext, handleDrawerFetchNext),
    takeEvery(setDrawerFilter, handleFilterChange),
    takeLatest(updateDrawerRoomLastMsg, handleUpdateDrawerRoomLastMsg),
    takeLatest(updateDrawerRoomFlagState, handleUpdateDrawerRoomFlagState),
    takeLatest(maybeClearRequestsCounter, handleMaybeClearRequestsCount),
    takeEvery(clearAllDrawerStreams, handleClearAllDrawerStreams),
    takeEvery(reloadDrawerStream, handleReloadDrawerStream),
  ]);
}

/**
 * Generates fake conversations data for testing purposes
 */
function generateFakeConversationsResponse() {
  // Use different users from sample data for DM and message fields
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line global-require
    const users = require('@wix/da-sample-data').users;

    const dmUser1 = users[0]; // chris
    const msgUser1 = users[2]; // Merf
    const dmUser2 = users[4]; // wlop
    const msgUser2 = users[5]; // sakimichan

    const fakeConversations: Database['damz']['Functions']['get_conversations']['Returns'] = [
      {
        name: '',
        room_type: 1,
        updated_at: new Date().toISOString(),
        room_id: 'fake-room-1',
        flags: 0,
        user_id: 'fake-user-1',
        user_count: 2,
        owner_id: dmUser1.useridUuid,
        dm_username: dmUser1.username,
        dm_usericon: 1,
        dm_symbol: dmUser1.type,
        dm_user_id: dmUser1.useridUuid,
        dm_user_id_int: 1,
        dm_user_banned: false,
        msg: 'Hey everyone, how is everyone doing today?',
        msg_username: msgUser1.username,
        msg_usericon: dmUser1.userid,
        msg_symbol: dmUser1.type,
        msg_user_id: msgUser1.useridUuid,
        msg_user_id_int: msgUser1.userid,
        msg_user_banned: false,
        msg_deleted_at: '',
        multi_icon: '',
        multi_username: '',
      },
      {
        name: 'Random',
        room_type: 2,
        updated_at: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
        room_id: 'fake-room-2',
        flags: 0,
        user_id: 'fake-user-2',
        user_count: 3,
        owner_id: dmUser2.useridUuid,
        dm_username: dmUser2.username,
        dm_usericon: 2,
        dm_symbol: dmUser2.type,
        dm_user_id: dmUser2.useridUuid,
        dm_user_id_int: dmUser2.userid,
        dm_user_banned: false,
        msg: 'Check out this interesting article I found!',
        msg_username: msgUser2.username,
        msg_usericon: 2,
        msg_symbol: msgUser2.type,
        msg_user_id: msgUser2.useridUuid,
        msg_user_id_int: msgUser2.userid,
        msg_user_banned: false,
        msg_deleted_at: '',
        multi_icon: '10,20',
        multi_username: `${dmUser2.username},${msgUser2.username}`,
      },
    ];

    return {
      data: fakeConversations,
      error: null,
    };
  }
}
